import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { findRegion } from "../i18n/regions";
import { findCurrency, currencyLocale } from "../i18n/currencies";

// Default preferences before the server responds. Each field is independent.
const DEFAULTS = {
    language:       "en",
    region:         "GB",
    currency:       "GBP",
    timezone:       Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London",
    date_format:    "DMY",
    time_format:    "24h",
    number_format:  "1,234.56",
    week_start:     1,
    ai_language:    "",
    document_translation: "keep",
};

const PrefsContext = createContext({ prefs: DEFAULTS, updatePrefs: async () => {}, ready: false });

// The pref cache is namespaced per user so switching accounts on the same
// browser doesn't briefly render another user's language/currency during
// hydration. The legacy `vu_prefs` key is still read as a first-launch
// fallback for anonymous visitors (landing / login pages).
function cacheKeyFor(userId) {
    return userId ? `vu_prefs_${userId}` : "vu_prefs";
}
function readCache(userId) {
    try {
        const raw = localStorage.getItem(cacheKeyFor(userId));
        if (raw) return JSON.parse(raw);
        // First-time login on this browser: fall through to defaults, not the
        // previous user's cache.
        return null;
    } catch { return null; }
}
function writeCache(userId, prefs) {
    try { localStorage.setItem(cacheKeyFor(userId), JSON.stringify(prefs)); } catch { /* ignore */ }
}

export function PrefsProvider({ children }) {
    const { user } = useAuth();
    const { i18n } = useTranslation();
    // Anonymous first paint uses whatever the last logged-out session had
    // (safe: no PII, just language for the landing page).
    const [prefs, setPrefs] = useState(() => {
        try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem("vu_prefs") || "{}")) }; }
        catch { return DEFAULTS; }
    });
    const [ready, setReady] = useState(false);

    // Hydrate from the server once we have a user
    useEffect(() => {
        if (!user) {
            // Logged out — reset to defaults so the next user doesn't inherit
            // stale values before their own hydration finishes.
            setPrefs((p) => ({ ...DEFAULTS, language: p.language || DEFAULTS.language }));
            setReady(true);
            return;
        }
        // Load this user's per-account cache first for instant paint, then
        // hydrate from the server. This still avoids the previous user's cache.
        const cached = readCache(user.user_id);
        const startingPoint = cached ? { ...DEFAULTS, ...cached } : { ...DEFAULTS };
        setPrefs(startingPoint);
        api.get("/auth/me/preferences").then(({ data }) => {
            const live = Object.fromEntries(
                Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined)
            );
            const merged = { ...DEFAULTS, ...cached, ...live };
            setPrefs(merged);
            writeCache(user.user_id, merged);
            // Legacy key mirrors the current user's language so the landing
            // page opens in the same locale next time (best-effort).
            localStorage.setItem("vu_prefs", JSON.stringify({ language: merged.language }));
            if (merged.language && merged.language !== i18n.language) {
                i18n.changeLanguage(merged.language);
            }
            setReady(true);
        }).catch(() => setReady(true));
    }, [user?.user_id, i18n]);

    // Keep <html lang> + direction in sync
    useEffect(() => {
        document.documentElement.lang = prefs.language || "en";
        const langDef = findRegion(prefs.region);
        document.documentElement.dir = (prefs.language === "ar") ? "rtl" : "ltr";
        void langDef;
    }, [prefs.language, prefs.region]);

    const updatePrefs = useCallback(async (patch) => {
        const next = { ...prefs, ...patch };
        setPrefs(next);
        if (user) {
            writeCache(user.user_id, next);
            localStorage.setItem("vu_prefs", JSON.stringify({ language: next.language }));
        } else {
            localStorage.setItem("vu_prefs", JSON.stringify(next));
        }
        if (patch.language && patch.language !== i18n.language) {
            i18n.changeLanguage(patch.language);
        }
        if (user) {
            try { await api.put("/auth/me/preferences", patch); } catch { /* ignore — cache is the source of truth */ }
        }
    }, [prefs, user, i18n]);

    const value = useMemo(() => ({ prefs, updatePrefs, ready }), [prefs, updatePrefs, ready]);
    return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export const usePrefs = () => useContext(PrefsContext);

// ----- Formatters powered by Intl + the user's preferences --------------------
export function useFormat() {
    const { prefs } = usePrefs();
    const region = findRegion(prefs.region);
    const locale = region.locale || "en-GB";
    return useMemo(() => {
        const numberFmt = makeNumberFormatter(locale, prefs.number_format);
        const fmtCurrency = (amount, currencyCode) => {
            const code = currencyCode || prefs.currency || "GBP";
            const def = findCurrency(code);
            // Prefer the currency's native locale so USD renders as "$1,234.56",
            // EUR as "1.234,56 €", JPY as "¥1,235", etc. Falls back to the
            // user's regional locale for unmapped currencies.
            const curLocale = currencyLocale(code, prefs.language) || locale;
            try {
                return new Intl.NumberFormat(curLocale, {
                    style: "currency", currency: code,
                    ...(def.decimals != null ? { minimumFractionDigits: def.decimals, maximumFractionDigits: def.decimals } : {}),
                }).format(Number(amount) || 0);
            } catch {
                return `${def.symbol}${Number(amount).toFixed(def.decimals ?? 2)}`;
            }
        };
        const fmtNumber = (n) => numberFmt.format(Number(n) || 0);
        const fmtDate = (input, { withTime = false } = {}) => {
            if (!input) return "";
            const d = input instanceof Date ? input : new Date(input);
            if (isNaN(d)) return String(input);
            const opts = dateOpts(prefs.date_format, prefs.time_format, withTime);
            try { return new Intl.DateTimeFormat(locale, { ...opts, timeZone: prefs.timezone }).format(d); }
            catch { return d.toLocaleString(); }
        };
        const fmtTime = (input) => {
            if (!input) return "";
            const d = input instanceof Date ? input : new Date(input);
            if (isNaN(d)) return String(input);
            try {
                return new Intl.DateTimeFormat(locale, {
                    hour: "2-digit", minute: "2-digit",
                    hour12: prefs.time_format === "12h",
                    timeZone: prefs.timezone,
                }).format(d);
            } catch { return d.toLocaleTimeString(); }
        };
        return { fmtCurrency, fmtNumber, fmtDate, fmtTime, locale, currency: prefs.currency };
    }, [locale, prefs.currency, prefs.language, prefs.date_format, prefs.time_format, prefs.number_format, prefs.timezone]);
}

function makeNumberFormatter(locale, style) {
    // Allow user to override locale-default thousands/decimal style.
    if (style === "1.234,56") return new Intl.NumberFormat("de-DE");
    if (style === "1 234.56") return new Intl.NumberFormat("fr-CA");
    if (style === "1,234.56") return new Intl.NumberFormat("en-US");
    return new Intl.NumberFormat(locale);
}

function dateOpts(dateFmt, timeFmt, withTime) {
    const base = { day: "numeric", month: "long", year: "numeric" };
    if (dateFmt === "DMY") Object.assign(base, { day: "2-digit", month: "2-digit", year: "numeric" });
    if (dateFmt === "MDY") Object.assign(base, { day: "2-digit", month: "2-digit", year: "numeric" });
    if (dateFmt === "YMD") Object.assign(base, { day: "2-digit", month: "2-digit", year: "numeric" });
    if (withTime) Object.assign(base, { hour: "2-digit", minute: "2-digit", hour12: timeFmt === "12h" });
    return base;
}
